<template>
  <div>
    <scraping-core ref="scrapingCore" :format="format" :data="data" :client="client" :datasource="ds" />

    <b-modal v-model="dataModal" title="Données du produit" size="lg" ok-only ok-title="Fermer">
      <div v-if="dataModalRow">
        <div class="mb-2">
          <span class="badge badge-secondary mr-1">{{ dataModalRow.nom_fr || dataModalRow.reference || '—' }}</span>
          <span v-if="dataModalRow.source" class="badge badge-info mr-1">{{ dataModalRow.source }}</span>
        </div>
        <table class="table table-sm table-bordered">
          <thead class="thead-light">
            <tr><th style="width:40%">Colonne</th><th>Valeur</th></tr>
          </thead>
          <tbody>
            <tr v-for="(val, key) in dataModalFields" :key="key">
              <td class="font-weight-bold">{{ key }}</td>
              <td style="word-break:break-all">{{ val }}</td>
            </tr>
          </tbody>
        </table>
      </div>
    </b-modal>
  </div>
</template>

<script>
export default {
  builder_info: 'Interface de suivi scraping produits: filtres, configuration des scrapers, lancement de recherche URL et validation des résultats par domaine/type.',
  props: ['format', 'data', 'client'],

  data() {
    return {
      dataModal: false,
      dataModalRow: null,
    };
  },

  computed: {
    dataModalFields() {
      if (!this.dataModalRow) return {};
      const catsMap = this.$refs.scrapingCore?.categoriesMap || {};
      const out = {};
      const SKIP = new Set(['categories', 'categorie_principale', 'caracteristiques', '__ob__']);
      for (const [k, v] of Object.entries(this.dataModalRow)) {
        if (k.startsWith('_') || SKIP.has(k)) continue;
        out[k] = v;
      }
      // Catégories
      if (this.dataModalRow.categories) {
        try {
          const cats = typeof this.dataModalRow.categories === 'string'
            ? JSON.parse(this.dataModalRow.categories)
            : this.dataModalRow.categories;
          out['categories'] = Array.isArray(cats) ? cats.map(id => catsMap[id] || id).join(', ') : cats;
        } catch (e) { out['categories'] = this.dataModalRow.categories; }
      }
      if (this.dataModalRow.categorie_principale != null) {
        out['categorie_principale'] = catsMap[this.dataModalRow.categorie_principale] || this.dataModalRow.categorie_principale;
      }
      // Caractéristiques
      if (this.dataModalRow.caracteristiques) {
        try {
          const caracts = typeof this.dataModalRow.caracteristiques === 'string'
            ? JSON.parse(this.dataModalRow.caracteristiques)
            : this.dataModalRow.caracteristiques;
          for (const c of (caracts || [])) {
            out['📋 ' + (c.name || c.feature_type_id)] = c.value || '';
          }
        } catch (e) {}
      }
      return out;
    },

    ds() {
      return {
        table: 'produit_reference',
        baseWhere: "source IS NOT NULL AND source != 'xchange' AND source != 'plugivery' AND IFNULL(`delete_origami`, 0) != 1",
        scrapingJoinId: 'produit_reference.id',
        selectFields: 'id, id_origami, status_origami, reference, nom_fr, marque, ean13, manufacturer_code, prix_ht, prix_ttc, prix_promo, quantite, categories, categorie_principale, source, id_source, caracteristiques',
        selectFieldsJoined: 'produit_reference.id, produit_reference.id_origami, produit_reference.status_origami, produit_reference.reference, produit_reference.nom_fr, produit_reference.marque, produit_reference.ean13, produit_reference.manufacturer_code, produit_reference.prix_ht, produit_reference.prix_ttc, produit_reference.prix_promo, produit_reference.quantite, produit_reference.categories, produit_reference.categorie_principale, produit_reference.source, produit_reference.id_source, produit_reference.caracteristiques',
        categoryPrimaryField: 'categorie_principale',
        listingColumns: [
          { key: 'id_origami',    label: 'id_origami' },
          { key: 'source',        label: 'Source' },
          { key: 'id_source',     label: 'id_source' },
          { key: 'status_origami', label: 'Statut' },
        ],
        conditionTestSourceCol: true,
        showSourceInVeilleTarifaire: true,
        conditionTestSelect: 'id, reference, nom_fr, marque, ean13, manufacturer_code, prix_ht, prix_ttc, categories, categorie_principale, source',
        scrapTestSelect: 'id, reference, nom_fr, marque, ean13, manufacturer_code, prix_ht, prix_ttc, categories, categorie_principale, source',
        productColumnsTable: 'produit_reference',
        defaultProductColumns: [
          'id_prestashop', 'id_origami', 'reference', 'nom_fr', 'nom_en', 'nom_es',
          'ean13', 'marque', 'status_origami', 'prix_ht', 'prix_ttc', 'quantite',
          'categorie_principale', 'categories',
        ],
        marquesTable: 'produit_reference',
        promoField: 'prix_promo',
        nomSqlTransform: false,
        jsonColumns: [],
        introText: "Le scraping et la veille tarifaire ne concernent que les produits ayant une source et ne faisant pas partie des logiciels xchange et plugivery.",
        categoriesSelect: 'id, name_fr',
        categoriesIsJson: false,
        getNom: (product) => product.nom_fr || product.reference || '—',
        getCleProduir: () => '',
        qbFields: [
          { value: 'marque',            label: 'Marque',               type: 'text', useMarquesList: true },
          { value: 'ean13',             label: 'EAN13',                type: 'text' },
          { value: 'manufacturer_code', label: 'Code fabricant',       type: 'text' },
          { value: 'prix_ht',           label: 'Prix HT',              type: 'number' },
          { value: 'prix_ttc',          label: 'Prix TTC',             type: 'number' },
          { value: 'categorie',         label: 'Catégorie principale', type: 'categorie' },
          { value: 'categories',        label: 'Catégories (toutes)',  type: 'categories' },
          { value: 'source',            label: 'Source',               type: 'text' },
        ],
        qbMainFilters: [
          { id: 'id',                   label: 'id',               type: 'integer' },
          { id: 'id_origami',           label: 'id_origami',       type: 'integer' },
          { id: 'reference',            label: 'Référence',        type: 'string'  },
          { id: 'nom_fr',               label: 'Nom (FR)',         type: 'string'  },
          { id: 'marque',               label: 'Marque',           type: 'string'  },
          { id: 'status_origami',       label: 'Statut',           type: 'string'  },
          { id: 'ean13',                label: 'EAN13',            type: 'string'  },
          { id: 'categorie_principale', label: 'Catégorie principale', type: 'integer' },
          { id: 'prix_ht',              label: 'Prix HT',          type: 'double'  },
          { id: 'prix_ttc',             label: 'Prix TTC',         type: 'double'  },
          { id: 'prix_promo',           label: 'Prix promo',       type: 'double'  },
          { id: 'quantite',             label: 'Quantité',         type: 'integer' },
          { id: 'categorie_nom',        label: 'Dans les catégories (nom)',  type: 'string'  },
          { id: 'source',               label: 'Source',           type: 'string'  },
          { id: 'manufacturer_code',     label: 'Code fabricant',   type: 'string'  },
        ],
        qbTestFilters: [
          { id: 'id',            label: 'id',             type: 'integer' },
          { id: 'nom_fr',        label: 'Nom (FR)',       type: 'string'  },
          { id: 'marque',        label: 'Marque',         type: 'string'  },
          { id: 'ean13',         label: 'EAN13',          type: 'string'  },
          { id: 'manufacturer_code',    label: 'Code fabricant',   type: 'string'  },
          { id: 'prix_ht',       label: 'Prix HT',        type: 'double'  },
          { id: 'prix_ttc',      label: 'Prix TTC',       type: 'double'  },
          { id: 'source',             label: 'Source',           type: 'string'  },
          { id: 'categorie_nom',        label: 'Dans les catégories (nom)',  type: 'string'  },
          { id: 'categorie_principale', label: 'Catégorie principale', type: 'integer' },
        ],

        getModalProductData: (product, catsMap = {}) => {
          const out = {};
          const LABELS = {
            id: 'id', id_origami: 'id_origami', status_origami: 'Statut',
            reference: 'Référence', nom_fr: 'Nom (FR)', marque: 'Marque',
            ean13: 'EAN13', manufacturer_code: 'Code fabricant',
            prix_ht: 'Prix HT', prix_ttc: 'Prix TTC', prix_promo: 'Promo',
            quantite: 'Quantité', source: 'Source', id_source: 'id_source',
          };
          const SKIP = new Set(['categories', 'categorie_principale', 'caracteristiques', '__ob__']);
          for (const [k, v] of Object.entries(product)) {
            if (k.startsWith('_') || SKIP.has(k)) continue;
            out[LABELS[k] || k] = v ?? '';
          }
          // Catégories
          if (product.categories) {
            try {
              const cats = typeof product.categories === 'string' ? JSON.parse(product.categories) : product.categories;
              out['Catégories'] = Array.isArray(cats) ? cats.map(id => catsMap[id] || id).join(', ') : cats;
            } catch (e) { out['Catégories'] = product.categories; }
          }
          if (product.categorie_principale != null) {
            out['Catégorie principale'] = catsMap[product.categorie_principale] || product.categorie_principale;
          }
          // Caractéristiques : une ligne par caract
          if (product.caracteristiques) {
            try {
              const caracts = typeof product.caracteristiques === 'string'
                ? JSON.parse(product.caracteristiques)
                : product.caracteristiques;
              for (const c of (caracts || [])) {
                out['📋 ' + (c.name || c.feature_type_id)] = c.value || '';
              }
            } catch (e) {}
          }
          return out;
        },
        rowActions: [
          { key: 'view-data', icon: 'fas fa-table', label: 'Voir les données', variant: 'outline-info', onClick: (row) => { this.dataModalRow = row; this.dataModal = true; } },
        ],
      };
    },
  },
};
</script>
